import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { DesignLayoutType, EventPageType } from '@graphql/generated-base';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useMemo } from 'react';
import { useEventDesignLayoutType } from './useEventDesignLayoutType/useEventDesignLayoutType';
import { useIsMapPageVisible, withoutHiddenMapPage } from './weddingMapPageVisibility';

type UseGetGuestSitePagesArgs = Readonly<{
  pages?: readonly EventPageFragment[];
  layoutType?: DesignLayoutType | null;
  /** Reads nothing: for a caller that has no V1 pages to filter. */
  skip?: boolean;
}>;

type UseGetGuestSitePagesReturn = Readonly<{
  pages: readonly EventPageFragment[];
}>;

// Filtering logic moved from BE to FE to avoid presentational logic on BE
export const useGetGuestSitePages = ({ pages = [], layoutType: layoutTypeProp, skip = false }: UseGetGuestSitePagesArgs = {}): UseGetGuestSitePagesReturn => {
  const { eventInfo } = useEventInfo();
  const { layoutType: fetchedLayoutType } = useEventDesignLayoutType({ skip: skip || layoutTypeProp != null });
  const layoutType = layoutTypeProp ?? fetchedLayoutType;

  /*
    The Wedding Map page is dropped for anyone outside the rollout.

    The guest-site layouts filter it in `useGuestSitePages`, but that is not
    the only list a nav is built from: the Registry guest page renders the
    same Brannan nav from this hook, so without this a flag-off guest on
    `/:handle/registry` would still see a Map entry linking into a route that
    sends them straight back to the guest site. Ecard, SaveTheDate and
    RsvpBlock read this hook too, and the same answer is right for all of
    them.

    The read records no exposure — see `useIsMapPageVisible`.
  */
  const isMapPageVisible = useIsMapPageVisible();

  const currentPages = useMemo(() => {
    if (!eventInfo) {
      return [];
    }

    const { qanda, story } = eventInfo;

    const tidbitsExist = qanda?.length && qanda.length > 0;
    const storyExists = !!story;
    let currentPages = pages;

    if (layoutType === DesignLayoutType.brannan && !tidbitsExist && !storyExists) {
      currentPages = currentPages.filter(page => page.type !== EventPageType.story);
    }

    if (layoutType === DesignLayoutType.aloha) {
      if (!tidbitsExist) {
        currentPages = currentPages.filter(page => page.type !== EventPageType.tidbits);
      }

      if (!storyExists) {
        currentPages = currentPages.filter(page => page.type !== EventPageType.story);
      }
    }

    // Keeps the array by reference when nothing drops, which is every event
    // today; see `withoutHiddenMapPage`.
    currentPages = withoutHiddenMapPage(currentPages, isMapPageVisible);

    return currentPages;
  }, [eventInfo, pages, layoutType, isMapPageVisible]);

  return {
    pages: currentPages,
  };
};
